import { FormEvent, useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ChangePasswordDialogProps = {
  triggerClassName?: string;
};

type Feedback = {
  type: "success" | "error";
  message: string;
} | null;

export function ChangePasswordDialog({ triggerClassName }: ChangePasswordDialogProps) {
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const resetForm = () => {
    setCurrentPassword("");
    setPassword("");
    setPasswordConfirmation("");
    setFeedback(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && !submitting) {
      resetForm();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);

    if (!currentPassword || !password || !passwordConfirmation) {
      setFeedback({ type: "error", message: "Preencha todos os campos." });
      return;
    }

    if (password !== passwordConfirmation) {
      setFeedback({ type: "error", message: "A confirmação da nova senha não confere." });
      return;
    }

    const csrfToken = document
      .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.getAttribute("content");

    if (!csrfToken) {
      setFeedback({
        type: "error",
        message: "Não foi possível validar esta sessão. Atualize a página e tente novamente.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const body = new URLSearchParams({
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      });

      const response = await fetch("/password", {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-TOKEN": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
        },
        body: body.toString(),
      });

      if (response.ok) {
        setCurrentPassword("");
        setPassword("");
        setPasswordConfirmation("");
        setFeedback({ type: "success", message: "Senha alterada com sucesso." });
        return;
      }

      if (response.status === 422) {
        const payload = await response.json().catch(() => null);
        const errors = payload?.errors ?? {};

        if (errors.current_password?.length) {
          setFeedback({ type: "error", message: "A senha atual não confere." });
          return;
        }

        if (errors.password?.length) {
          setFeedback({
            type: "error",
            message: "A nova senha não atende aos requisitos de segurança ou a confirmação não confere.",
          });
          return;
        }
      }

      if (response.status === 401 || response.status === 419) {
        setFeedback({
          type: "error",
          message: "Sua sessão expirou. Atualize a página, entre novamente e tente de novo.",
        });
        return;
      }

      setFeedback({
        type: "error",
        message: "Não foi possível alterar a senha. Tente novamente.",
      });
    } catch {
      setFeedback({
        type: "error",
        message: "Não foi possível conectar ao servidor. Tente novamente.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <button type="button" onClick={() => setOpen(true)} className={triggerClassName}>
        <KeyRound className="h-4 w-4 text-muted-foreground" />
        Alterar senha
      </button>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Alterar senha</DialogTitle>
          <DialogDescription>
            Informe sua senha atual e escolha uma nova senha para sua conta AllePets.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="change-password-current">Senha atual</Label>
            <Input
              id="change-password-current"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="change-password-new">Nova senha</Label>
            <Input
              id="change-password-new"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={submitting}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="change-password-confirmation">Confirmar nova senha</Label>
            <Input
              id="change-password-confirmation"
              type="password"
              autoComplete="new-password"
              value={passwordConfirmation}
              onChange={(event) => setPasswordConfirmation(event.target.value)}
              disabled={submitting}
              required
            />
          </div>

          {feedback && (
            <p
              role="status"
              className={`text-sm ${
                feedback.type === "success" ? "text-green-700" : "text-destructive"
              }`}
            >
              {feedback.message}
            </p>
          )}

          <p className="text-xs text-muted-foreground">
            Use uma senha forte e diferente das utilizadas em outros serviços.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar nova senha
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
